import { observer } from "mobx-react";
import * as React from "react";
import { NodeCollectionStore, NodeStore, StoreType } from "../../stores";
import { CANVAS_SIZE, COLORS, HALF, LINK_HEIGHT, LINK_SPACING, LINK_WIDTH, ORIGIN, RADIANS_TO_DEGREES } from "../../Constants";

interface LinkLayerProps {
    root: NodeCollectionStore;
}

/**
 * This class renders visual links between nodes. The chain style rendering is from a 
 * formula I found online that segments lines between two points and rotating 
 * each segment using vector normalization and arctangent functions.
 */
@observer
export class LinkLayer extends React.Component<LinkLayerProps> {

    /**
     * This private helper method recursively collects all nodes from a given collection 
     * so links can exist between nodes inside the collection.
     * @param collection The collection to collect nodes from.
     * @param out The collection to put links into.
     */
    private collectNodes(collection: NodeCollectionStore, out: NodeStore[]) {
        for (const node of collection.nodes) {
            out.push(node);
            if (node.type === StoreType.Collection) {
                this.collectNodes(node as NodeCollectionStore, out);
            }
        }
    }

    /**
     * This private helper method renders a chain of links between two points.
     * @param top The top world position of the node center.
     * @param bottom The bottom world position of the node center.
     * @param key The key to use for the chain.
     * @returns An array of elements representing the chain of links.
     */
    private renderChain(
    top: { x: number; y: number },
    bottom: { x: number; y: number },
    key: string
) {
    const deltaX = bottom.x - top.x;
    const deltaY = bottom.y - top.y;
    const length = Math.hypot(deltaX, deltaY);
    if (length === 0) return null;

    const angle = Math.atan2(deltaY, deltaX) * (RADIANS_TO_DEGREES);
    const spacing = LINK_SPACING;
    const linkWidth = LINK_WIDTH;
    const linkHeight = LINK_HEIGHT;   
    const count = Math.floor(length / spacing);

    const links: JSX.Element[] = [];

    for (let i = 0; i <= count; i++) {
        const link = i * spacing;
        const x = top.x + (deltaX / length) * link;
        const y = top.y + (deltaY / length) * link;

        links.push(
            <rect
                key={`${key}-${i}`}
                x={-linkWidth * HALF}
                y={-linkHeight * HALF}
                width={linkWidth}
                height={linkHeight}
                rx={linkHeight * HALF}
                ry={linkHeight * HALF}
                fill={COLORS.LINK_COLOR}
                transform={`
                    translate(${x}, ${y})
                    rotate(${angle})
                `}
            />
        );
    }

    return links;
}

    /**
     * This method renders the link layer as an SVG overlay.
     * @returns JSX element containing all rendered link chains
     */
    render() {
        const nodes: NodeStore[] = [];
        this.collectNodes(this.props.root, nodes);

        const seen = new Set<string>();
        const chains: JSX.Element[] = [];

        for (const node of nodes) {
            for (const linkedId of node.links) {
                const key = [node.Id, linkedId].sort().join("-");
                if (seen.has(key)) continue;
                seen.add(key);

                const target = nodes.find(n => n.Id === linkedId);
                if (!target) continue;

                const a = node.getWorldCenter();
                const b = target.getWorldCenter();

                chains.push(
                    <g key={key}>
                        {this.renderChain(a, b, key)}
                    </g>
                );
            }
        }

        return (
            <svg
                width={CANVAS_SIZE}
                height={CANVAS_SIZE}
                style={{
                    position: "absolute",
                    top: ORIGIN,
                    left: ORIGIN,
                    pointerEvents: "none",
                }}
            >
                {chains}
            </svg>
        );
    }
}